import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Plugs, PlugsConnected, WarningCircle, PencilSimple, Trash, Key, UserPlus, UserMinus, CreditCard, MagnifyingGlass } from "@phosphor-icons/react";

const CATEGORIES = [
  { key: "", label: "All" },
  { key: "connection", label: "Connections" },
  { key: "agent", label: "Agent errors" },
  { key: "audit", label: "Account actions" },
];

const META = {
  "machine.online": { icon: PlugsConnected, cls: "text-emerald-400", badge: "bg-emerald-500/10 text-emerald-300" },
  "machine.offline": { icon: Plugs, cls: "text-zinc-400", badge: "bg-zinc-500/10 text-zinc-300" },
  "agent.error": { icon: WarningCircle, cls: "text-red-400", badge: "bg-red-500/10 text-red-300" },
  "agent.info": { icon: WarningCircle, cls: "text-blue-400", badge: "bg-blue-500/10 text-blue-300" },
  "machine.added": { icon: PencilSimple, cls: "text-blue-400", badge: "bg-blue-500/10 text-blue-300" },
  "machine.renamed": { icon: PencilSimple, cls: "text-blue-400", badge: "bg-blue-500/10 text-blue-300" },
  "machine.deleted": { icon: Trash, cls: "text-red-400", badge: "bg-red-500/10 text-red-300" },
  "token.regenerated": { icon: Key, cls: "text-amber-400", badge: "bg-amber-500/10 text-amber-300" },
  "teammate.invited": { icon: UserPlus, cls: "text-cyan-400", badge: "bg-cyan-500/10 text-cyan-300" },
  "teammate.removed": { icon: UserMinus, cls: "text-zinc-400", badge: "bg-zinc-500/10 text-zinc-300" },
  "plan.changed": { icon: CreditCard, cls: "text-violet-400", badge: "bg-violet-500/10 text-violet-300" },
  "plan.purchased": { icon: CreditCard, cls: "text-emerald-400", badge: "bg-emerald-500/10 text-emerald-300" },
};
const fallback = { icon: WarningCircle, cls: "text-zinc-400", badge: "bg-zinc-500/10 text-zinc-300" };

const fmt = (s) => {
  const d = new Date(s);
  if (isNaN(d)) return "—";
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
};

export function ActivityTab() {
  const [category, setCategory] = useState("");
  const [q, setQ] = useState("");
  const [events, setEvents] = useState([]);
  const [nextBefore, setNextBefore] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (opts = {}) => {
    const { before, reset } = opts;
    if (reset) setLoading(true);
    try {
      const params = { limit: 60 };
      if (category) params.category = category;
      if (q.trim()) params.q = q.trim();
      if (before) params.before = before;
      const res = await api.get("/admin/events", { params });
      setEvents((prev) => (before ? [...prev, ...res.data.events] : res.data.events));
      setNextBefore(res.data.next_before);
    } catch {
      /* noop */
    } finally {
      setLoading(false);
    }
  }, [category, q]);

  useEffect(() => {
    const t = setTimeout(() => load({ reset: true }), 250);
    return () => clearTimeout(t);
  }, [category, q, load]);

  return (
    <div data-testid="admin-activity" className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center justify-between">
        <div className="flex flex-wrap gap-1.5">
          {CATEGORIES.map((c) => (
            <button
              key={c.key || "all"}
              data-testid={`activity-filter-${c.key || "all"}`}
              onClick={() => setCategory(c.key)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${category === c.key ? "bg-blue-600 text-white" : "bg-white/5 text-zinc-400 hover:text-zinc-200 border border-white/10"}`}
            >
              {c.label}
            </button>
          ))}
        </div>
        <div className="relative w-full sm:w-64">
          <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input
            data-testid="activity-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search machine, email, action…"
            className="w-full h-9 pl-9 pr-3 rounded-md bg-white/5 border border-white/10 text-sm text-white placeholder:text-zinc-500 focus:border-blue-500 focus:outline-none"
          />
        </div>
      </div>

      <p className="text-xs text-zinc-600">Logs are kept for 30 days.</p>

      {loading ? (
        <div className="text-zinc-500 font-mono text-sm animate-pulse py-8">Loading activity…</div>
      ) : events.length === 0 ? (
        <div className="text-zinc-500 text-sm py-10 text-center border border-white/10 rounded-lg">No activity recorded yet.</div>
      ) : (
        <div className="border border-white/10 rounded-lg overflow-hidden divide-y divide-white/5">
          {events.map((e) => {
            const m = META[e.type] || fallback;
            const Icon = m.icon;
            return (
              <div key={e.event_id} className="flex items-start gap-3 px-4 py-3 hover:bg-white/[0.02]">
                <Icon size={18} className={`${m.cls} mt-0.5 shrink-0`} weight="fill" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm text-white">{e.message}</span>
                    <span className={`text-[10px] rounded-full px-1.5 py-0.5 ${m.badge}`}>{e.type}</span>
                  </div>
                  <div className="text-xs text-zinc-500 mt-0.5 truncate">
                    {e.machine_name && <span className="text-zinc-400">{e.machine_name}</span>}
                    {e.machine_name && (e.org_name || e.actor_email) && " · "}
                    {e.org_name && <span>{e.org_name}</span>}
                    {e.actor_email && <span> · by {e.actor_email}</span>}
                    {e.meta?.os && <span> · {e.meta.os}</span>}
                  </div>
                </div>
                <span className="text-xs text-zinc-500 shrink-0 whitespace-nowrap">{fmt(e.ts)}</span>
              </div>
            );
          })}
        </div>
      )}

      {nextBefore && !loading && (
        <div className="text-center">
          <Button
            data-testid="activity-load-more"
            variant="outline"
            onClick={() => load({ before: nextBefore })}
            className="border-white/10 bg-white/5 hover:bg-white/10 text-sm"
          >
            Load more
          </Button>
        </div>
      )}
    </div>
  );
}
